import { t } from 'i18n';
import React, { useEffect } from 'react';
import { useIdentifiedPage } from 'hooks';
import { useProductDetailState } from 'contexts';
import { trackViewDetail, ServiceContainer } from 'helpers';
import { Loading } from 'components/Loading';
import {
  Wrapper,
  Card,
  StyledBreadCrumb,
  Stock,
  DescriptionCol,
  ProductDescription,
  NotFoundProductLayout,
  Content,
  ProductVariantsWrapper,
} from 'components/ProductDetail/styles';
import { GetServerSideProps } from 'next';
import { Box, Flex } from 'reflexbox';
import { useCart } from 'contexts';
import { logger } from '@teko/log';
import { ProductDetailPromotion } from 'types/promotions';
import { FlashSaleBar, NoProductsFound } from '@teko/teko-ecommerce';
import { Col, Container, Divider } from '@teko/ds-desktop';
import { DividerStyleEnum } from '@teko/types/shared';
import { MetaData } from './MetaData';
import dynamic from 'next/dynamic';
const Policies = dynamic(() =>
  import('./Policies').then((mod) => mod.Policies),
);
const ProductRelatedPromotions = dynamic(() =>
  import('./ProductRelatedPromotions').then(
    (mod) => mod.ProductRelatedPromotions,
  ),
);
const SameBrandProducts = dynamic(() =>
  import('./SameBrandProducts').then((mod) => mod.SameBrandProducts),
);
const ProductWarningNote = dynamic(() =>
  import('./ProductWarningNote').then((mod) => mod.ProductWarningNote),
);
const ProductCombo = dynamic(() =>
  import('./ProductCombo').then((mod) => mod.ProductCombo),
);
const ProductHeader = dynamic(() =>
  import('./ProductHeader').then((mod) => mod.ProductHeader),
);
const ActionButtons = dynamic(() =>
  import('./ActionButtons').then((mod) => mod.ActionButtons),
);
const ProductImages = dynamic(() =>
  import('./ProductImages').then((mod) => mod.ProductImages),
);
const ProductVariants = dynamic(() =>
  import('./ProductVariants').then((mod) => mod.ProductVariants),
);
const ProductSpecifications = dynamic(() =>
  import('./ProductSpecifications').then((mod) => mod.ProductSpecifications),
);
const Description = dynamic(() =>
  import('./ProductDescription').then((mod) => mod.ProductDescription),
);
const ProductPriceAndPromotion = dynamic(() =>
  import('./ProductPriceAndPromotion').then(
    (mod) => mod.ProductPriceAndPromotion,
  ),
);
interface DetailPageProps {
  showRelatedPromotion?: boolean;
  extraPromotions?: ProductDetailPromotion[];
  warningNote?: React.ReactNode;
  policies?: React.ReactNode;
  customPrice?: React.ReactNode;
}
const renderFlashSaleBadge = (
  total: number,
  sold: number,
  stock: number,
  endAt: string,
) => {
  const remain = Math.min(total - sold, stock);
  const message =
    sold / total > 0.9 || remain < 5
      ? t('Sắp cháy hàng')
      : t('Còn lại {{remain}}', { remain: remain });
  return (
    <FlashSaleBar
      endAt={new Date(endAt)}
      text={message}
      margin={['s16', 's0']}
      padding="s8"
    />
  );
};
export const DetailPage: React.FC<DetailPageProps> = ({
  showRelatedPromotion = true,
  extraPromotions = [],
  warningNote,
  policies,
  customPrice,
}: DetailPageProps) => {
  const { product, showStock, stockAvailable, showFlashSale } =
    useProductDetailState();
  const cart = useCart();
  const breadCrumbs = [
    {
      disabled: false,
      text: '',
      icon: {
        name: 'home',
        size: 16,
      },
      href: '/',
    },
    {
      disabled: true,
      text: product?.displayName || product?.name,
    },
  ];
  const flashSaleBadge =
    showFlashSale &&
    renderFlashSaleBadge(
      product.flashSale.total,
      product.flashSale.used,
      stockAvailable,
      product.flashSale.endAt,
    );
  useIdentifiedPage('productDetail');
  useEffect(() => {
    if (product) {
      trackViewDetail({
        price: product.price.price,
        name: product.name,
        sku: product.sku,
        brand: product.brand.name,
        category: product?.categories?.[0]?.name || '',
      });
    }
  }, [product?.sku]);
  if (!product) {
    return (
      <NotFoundProductLayout>
        <NoProductsFound show text={t('Không tìm thấy sản phẩm')} />
      </NotFoundProductLayout>
    );
  }
  return (
    <>
      <MetaData />
      <Loading isLoading={cart.loadingAddToCart}>
        <Container>
          <Wrapper>
            <StyledBreadCrumb
              items={breadCrumbs}
              divider=">"
              color="#333333"
              outline
            />
            <Flex marginBottom={24}>
              <Box width="75.6%" marginRight={16}>
                <Card style={{ padding: '24px' }}>
                  <Flex>
                    <Box width="40.5%" paddingRight={16}>
                      <ProductImages />
                      <Divider
                        dividerStyle={DividerStyleEnum.dashed}
                        margin={['s12', 's0']}
                        color="divider"
                      />
                      <Content
                        dangerouslySetInnerHTML={{
                          __html: product?.seoInfo?.shortDescription,
                        }}
                      />
                    </Box>
                    <Box width="59.5%">
                      {flashSaleBadge}
                      <ProductHeader />
                      <ProductVariantsWrapper>
                        <ProductVariants />
                      </ProductVariantsWrapper>
                      {showStock && (
                        <Stock>
                          {t('Chỉ còn {{totalAvailable}} sản phẩm', {
                            totalAvailable: product?.totalAvailable,
                          })}
                        </Stock>
                      )}
                      <ProductPriceAndPromotion customPrice={customPrice} />
                      <ActionButtons />
                      <ProductWarningNote warningNote={warningNote} />
                      {showRelatedPromotion && (
                        <ProductRelatedPromotions
                          extraPromotions={extraPromotions}
                        />
                      )}
                      <div className="productAfterPromoBox" />
                    </Box>
                  </Flex>
                </Card>
              </Box>
              <Box width="24.4%">
                <Policies policies={policies} />
                {/*<div className="productInlineRight" />*/}
              </Box>
            </Flex>
            <Flex marginBottom={24}>
              <Box width="75.6%" marginRight={16}>
                <ProductCombo />
              </Box>
            </Flex>
            <ProductDescription marginBottom="s24">
              <DescriptionCol span={8}>
                <Description />
              </DescriptionCol>
              <Col span={4}>
                <ProductSpecifications />
              </Col>
            </ProductDescription>
            <SameBrandProducts />
          </Wrapper>
        </Container>
      </Loading>
    </>
  );
};
export const getServerSidePropsDetail: GetServerSideProps = async (context) => {
  try {
    const terminalCode = (context.req as any).terminal;
    const location = (context.req as any).location;
    const { discoveryService } = new ServiceContainer(
      (context.req as any).tenantEnv,
    );
    const response = await discoveryService.getProduct(
      {
        sku: context.query.sku as string,
        terminalCode,
        location,
      },
      true,
    );
    return {
      props: {
        serverProduct: {
          product: response.result.product,
        },
        key: context.params.sku,
      },
    };
  } catch (e: any) {
    logger.error(context.req, e);
    return {
      notFound: true,
    };
  }
};
